import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, CircularProgress, MenuItem, Paper, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "../../api";
import RefundTransactionDialog, { money } from "./RefundTransactionDialog";

const STATUS_CHIPS = {
  pending: { label: "Awaiting decision", color: "warning" },
  processing: { label: "Refunding…", color: "info" },
  approved: { label: "Approved", color: "success" },
  rejected: { label: "Rejected", color: "default" },
  cancelled: { label: "Withdrawn", color: "default" },
};

const KIND_LABELS = {
  paper: "Past paper",
  mock_exam: "Mock exam",
  pdf_pack: "Practice paper",
  course_registration: "Course",
  course_installment: "Course instalment",
  mobile_subscription: "Mobile app",
};

/**
 * Customers' refund requests (sent from Plans & billing). An admin reviews each one:
 * approving issues the Stripe refund, rejecting emails the customer the reason.
 */
export default function RefundRequests({ onPendingCount }) {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const [status, setStatus] = useState("open");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page: page + 1, per_page: perPage };
      if (status) params.status = status;
      const res = await api.get("admin/billing/refund-requests", { params });
      const data = res.data?.data || {};
      setRows(data.data || []);
      setTotal(data.total || 0);
      onPendingCount?.(data.pending_count || 0);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load refund requests");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [page, perPage, status, onPendingCount]);

  useEffect(() => { load(); }, [load]);

  return (
    <Box>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
        <TextField
          select size="small" label="Status" value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(0); }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="open">Awaiting decision</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
          <MenuItem value="cancelled">Withdrawn</MenuItem>
          <MenuItem value="">All requests</MenuItem>
        </TextField>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 3, overflowX: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 700 }}>Requested</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Product</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Paid</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Reason</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => {
                const chip = STATUS_CHIPS[r.status] || { label: r.status, color: "default" };
                const open = r.status === "pending";
                return (
                  <TableRow key={r.id} hover>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      {r.created_at ? new Date(r.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : "—"}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.student_name || "—"}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.student_email}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{r.product}</Typography>
                      <Typography variant="caption" color="text.secondary">{KIND_LABELS[r.purchasable_type] || r.purchasable_type}</Typography>
                      {r.claim === "faulty" && (
                        <Chip size="small" color="error" variant="outlined" label="Faulty item" sx={{ ml: 1, height: 18 }} />
                      )}
                      {open && r.refund_policy && !r.refund_policy.eligible && r.claim !== "faulty"
                        && !["not_stripe", "refunded"].includes(r.refund_policy.reason_code) && (
                        <Typography variant="caption" display="block" color="warning.main">Outside the refund policy</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>{money(r.amount_paid, r.currency)}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>
                      <Tooltip title={r.reason} arrow>
                        <Typography variant="body2" noWrap>{r.reason}</Typography>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: "nowrap" }}>
                      <Chip size="small" label={chip.label} color={chip.color} />
                      {r.refund_policy?.subscription_usage && (
                        <Chip
                          size="small" variant="outlined" sx={{ ml: 1 }}
                          color={r.refund_policy.subscription_usage.used ? "error" : "success"}
                          label={r.refund_policy.subscription_usage.label}
                        />
                      )}
                      {r.status === "approved" && r.refunded_amount != null && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {money(r.refunded_amount, r.currency)} refunded{r.decided_by ? ` by ${r.decided_by}` : ""}
                        </Typography>
                      )}
                      {r.status === "rejected" && r.decided_by && (
                        <Typography variant="caption" display="block" color="text.secondary">by {r.decided_by}</Typography>
                      )}
                      {r.status === "rejected" && (r.rejection_summary || r.admin_note) && (
                        <Typography variant="caption" display="block" color="text.secondary" sx={{ maxWidth: 260 }}>
                          {r.rejection_summary || r.admin_note}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small" variant={open ? "contained" : "text"} color={open ? "primary" : "inherit"}
                        onClick={() => setSelected(r)}
                        sx={{ textTransform: "none" }}
                      >
                        {open ? "Review" : "View"}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ color: "text.secondary", py: 4 }}>
                    {status === "open" ? "No refund requests waiting for a decision" : "No refund requests found"}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={total}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            rowsPerPage={perPage}
            onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </TableContainer>
      )}

      <RefundTransactionDialog
        open={!!selected}
        row={selected}
        refundRequest={selected}
        onClose={() => setSelected(null)}
        onDone={load}
      />
    </Box>
  );
}
